import React, { useEffect, useCallback } from 'react';
import CommentToggle from '../CommentToggle/CommentToggle';
import CommentsList from '../FormApproveComponents/CommentsList/CommentsList';
import axiosInstance from '../../../axiosInstance';
import { ROLES } from '../../../constants/roles';
import { STATUSES } from '../../../constants/statuses';

/**
 * Committee Tab Component
 * Allows Committee role (previously RA) to add comments and attachments on requests
 * Follows the same pattern as ECTD tab
 */
const Committee = ({ 
  requestId = null,
  requestProgressId = null,
  role = '',
  requestProgressStatus = '',
  comments = [],
  setComments = () => {}
}) => {
  
  // Fetch comments from API
  useEffect(() => {
    const fetchComments = async () => {
      if (!requestId) return;
      
      try {
        const response = await axiosInstance.get(`/api/comments/${requestId}`);
        if (response?.data?.data) {
          setComments(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching comments:', error);
      }
    };
    
    fetchComments();
  }, [requestId, setComments]);

  // Handle adding a new comment - matches the form comment flow
  const handleAddComment = useCallback(async (comment, subFormId, fileId = null, fileData = null) => {
    try {
      const requestData = {
        subFormId: null, // Committee comments don't have subFormId
        comment,
        requestsProgressId: requestProgressId,
      };
      
      if (fileId) {
        requestData.additional_file_id = fileId;
      }

      const response = await axiosInstance.post(`/api/comments`, requestData);
      
      // set isOwner to true since the current user just created this comment
      const enrichedComment = { ...response.data, isOwner: true };
      if (fileData && fileId) {
        enrichedComment.additional_file = {
          id: fileData.id,
          original_name: fileData.original_name,
          file_path: null,
          file_size: fileData.file_size,
          mime_type: fileData.mime_type,
          createdAt: fileData.created_at,
          updatedAt: fileData.created_at,
          deletedAt: null,
          user_id: null,
          end_user_id: null,
          ...fileData
        };
      }
      
      setComments(prev => [...prev, enrichedComment]);
    } catch (error) {
      console.error('Error adding committee comment:', error);
      throw error;
    }
  }, [requestProgressId, setComments]);

  // handle edit comment
  const handleEditComment = useCallback(async (commentId, newText, attachmentInfo = {}) => {
    try {
      const requestData = { comment: newText };
      
      if (attachmentInfo.remove_attachment) {
        requestData.remove_attachment = true;
      } else if (attachmentInfo.new_file_id) {
        requestData.additional_file_id = attachmentInfo.new_file_id;
      }

      await axiosInstance.put(`/api/comments/${commentId}`, requestData);
      // refetch comments
      const response = await axiosInstance.get(`/api/comments/${requestId}`);
      if (response?.data?.data) {
        setComments(response.data.data);
      }
    } catch (error) {
      console.error('error editing comment:', error);
      throw error;
    }
  }, [requestId, setComments]);

  // handle delete comment
  const handleDeleteComment = useCallback(async (commentId) => {
    try {
      await axiosInstance.delete(`/api/comments/${commentId}`);
      setComments(prev => prev.filter(c => c.id !== commentId));
    } catch (error) {
      console.error('error deleting comment:', error);
      throw error;
    }
  }, [setComments]);

  return (
    <div>
      {/* Comments Section */}
      <div 
        className="form-comment-section" 
        style={{ 
          isolation: 'isolate', 
          position: 'relative', 
          zIndex: 1,
          marginTop: '35px'
        }}
      >
        {/* Display all comments - simple list like ECTD */}
        <CommentsList
          prevComments={comments}
          pagination={{}}
          filters={{}}
          updateFilters={() => {}}
          persona="admin"
          showControls={false}
          showFilters={false}
          showSorting={false}
          showPageSize={false}
          showResultsInfo={false}
          showPagination={false}
          onEdit={handleEditComment}
          onDelete={handleDeleteComment}
        />

        {/* Show Add Comment form for Committee when they have active status - open by default */}
        {role === ROLES.RA && requestProgressStatus === STATUSES.ROLE_STATUSES[ROLES.RA].ACTIVE && (
          <CommentToggle
            onAddComment={handleAddComment}
            persona="admin"
            buttonText="Comment"
            buttonProps={{ sx: { marginTop: 2, marginBottom: 2 } }}
            initiallyOpen={true}
            hasComments={comments && comments.length > 0}
          />
        )}
      </div>
    </div>
  );
};

export default Committee;

